import React, { useState } from "react";
import { Outlet, Link } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  useMediaQuery,
  Collapse,
  Divider,
  IconButton,
} from "@mui/material";
import { ExpandLess, ExpandMore, Menu as MenuIcon } from "@mui/icons-material";

const drawerWidth = 240;
const logoUrl =
  "https://brokepiggytoken.xyz/wp-content/uploads/2025/05/crowdlaunch-logo-white.png";

const docTopics = [
  {
    group: "Whitepaper",
    topics: [{ label: "CrowdLaunch Whitepaper", path: "/docs/whitepaper" }],
  },
  {
    group: "User Wallet & Navigation",
    topics: [
      { label: "Connect Your Wallet", path: "/docs/connect-wallet" },
      { label: "Platform Navigation & Menu", path: "/docs/navigation" },
    ],
  },
  {
    group: "Core Platform Features",
    topics: [
      { label: "Swap LNCH", path: "/docs/swap-lnch" },
      { label: "Dashboard Overview", path: "/docs/dashboard" },
      { label: "Buying a Tokenized Project", path: "/docs/buy-tokenized-project" },
    ],
  },
  {
    group: "Staking, Holdings & Profile",
    topics: [
      { label: "Pools & Rewards", path: "/docs/staking-lnch" },
      { label: "Managing Your Portfolio", path: "/docs/holdings" },
      { label: "Claiming Refunds", path: "/docs/claim-refunds" },
      { label: "Project Status", path: "/docs/project-status" },
      { label: "External Links", path: "/docs/leaving-crowdlaunch" },
      { label: "Manage Deployed Projects", path: "/docs/my-profile" },
    ],
  },
  {
    group: "Verification & Project Management",
    topics: [
      { label: "Updating KYC & Verification Details", path: "/docs/kyc-updates" },
      { label: "Document Edits & Uploads", path: "/docs/document-edits" },
      { label: "Finalizing, Canceling, & Withdrawing Shares", path: "/docs/project-management" },
      { label: "Verify Your Project (KYC & Document Upload)", path: "/docs/verify-project" },
      { label: "Terms & Agreements", path: "/docs/terms-agreements" },
    ],
  },
  {
    group: "Promotion & Listings",
    topics: [{ label: "Promote Your Project", path: "/docs/promote-project" }],
  },
];

const DocsLayout = () => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [openGroups, setOpenGroups] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleGroupClick = (group) => {
    setOpenGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  return (
    <Box sx={{ display: "flex" }}>
      {/* Hamburger Icon for Mobile (moved outside of the Drawer) */}
      {isMobile && !mobileOpen && (
  <IconButton
    color="inherit"
    aria-label="open drawer"
    onClick={handleDrawerToggle}
    sx={{
      position: "fixed",
      top: 10,
      left: 10,
      zIndex: (theme) => theme.zIndex.drawer + 1,
    }}
  >
    <MenuIcon />
  </IconButton>
)}


      {/* Side Menu Drawer */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={isMobile ? handleDrawerToggle : undefined}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            backgroundColor: "#1C1C1E",
          },
        }}
      >
        {/* Logo container with Docs subtitle */}
        <Box sx={{ px: 2, pt: 2 }}>
          <Box sx={{ position: "relative" }}>
            <Box
              component="img"
              src={logoUrl}
              alt="CrowdLaunch Logo"
              sx={{ width: "100%" }}
            />
            <Typography
              variant="subtitle2"
              sx={{
                position: "absolute",
                bottom: "4px",
                right: "4px",
                color: "#FFD700",
                fontSize: "0.85rem",
                fontWeight: "bold",
                m: 0,
              }}
            >
              Docs
            </Typography>
          </Box>
        </Box>

        {/* Navigation List with scrolling */}
        <Box
          sx={{
            mt: 1,
            px: 2,
            overflowY: "auto",
            height: "calc(100vh - 80px)",
            "::-webkit-scrollbar": {
              width: "6px",
            },
            "::-webkit-scrollbar-track": {
              background: "#1C1C1E",
            },
            "::-webkit-scrollbar-thumb": {
              background: "linear-gradient(45deg, #FFD700, #FFC107)",
              borderRadius: "6px",
            },
            "::-webkit-scrollbar-thumb:hover": {
              background: "linear-gradient(45deg, #FFC107, #FFA000)",
            },
            scrollbarWidth: "thin",
            scrollbarColor: "#FFC107 #1C1C1E",
          }}
        >
          <List>
            {docTopics.map((groupObj, idx) => (
              <Box key={idx}>
                {/* Group header */}
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleGroupClick(groupObj.group)}>
                    <ListItemText
                      primary={groupObj.group}
                      primaryTypographyProps={{
                        sx: {
                          color: "#FFFFFF",
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                        },
                      }}
                    />
                    {openGroups[groupObj.group] ? (
                      <ExpandLess sx={{ color: "#FFFFFF" }} />
                    ) : (
                      <ExpandMore sx={{ color: "#FFFFFF" }} />
                    )}
                  </ListItemButton>
                </ListItem>

                {/* Nested topics */}
                <Collapse in={openGroups[groupObj.group]} timeout="auto" unmountOnExit>
                  <Box sx={{ borderLeft: "2px solid #444", ml: 2, pl: 1 }}>
                    <List component="div" disablePadding>
                      {groupObj.topics.map((topic, tidx) => (
                        <ListItem key={tidx} disablePadding>
                          <ListItemButton
                            component={Link}
                            to={topic.path}
                            sx={{ pl: 1.5 }}
                          >
                            <ListItemText
                              primary={topic.label}
                              primaryTypographyProps={{
                                sx: { color: "#CCCCCC", fontSize: "0.75rem" },
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Collapse>

                {/* Divider between groups */}
                <Divider sx={{ bgcolor: "#333" }} />
              </Box>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 4 },
          backgroundColor: "#28282B",
          minHeight: "100vh",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default DocsLayout;
